import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Spin,
  Alert,
  Typography,
  Progress,
  Tag,
  Space
} from 'antd';
import {
  DashboardOutlined,
  ClockCircleOutlined,
  UserOutlined,
  DatabaseOutlined,
  WifiOutlined,
  HddOutlined,
  ThunderboltOutlined,
  CloudOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Option } = Select;
const { Title, Text } = Typography;

interface ServerInfo {
  id: number;
  name: string;
  status: string;
  game_name: string;
  node_name: string;
  node_host: string;
  port: number;
  max_players: number;
}

interface StatusData {
  timestamp: string;
  status: string;
  uptime: number;
  response_time: number;
}

interface ResourceData {
  timestamp: string;
  cpu_usage: number;
  memory_usage: number;
  disk_usage: number;
  network_in: number;
  network_out: number;
}

interface PlayerData {
  timestamp: string;
  online_players: number;
  max_players: number;
  player_join_rate: number;
  player_leave_rate: number;
}

interface MonitoringData {
  server: ServerInfo;
  statusHistory: StatusData[];
  resourceUsage: ResourceData[];
  playerStats: PlayerData[];
}

interface ServerMonitoringProps {
  serverId: string;
}

const ServerMonitoring: React.FC<ServerMonitoringProps> = ({ serverId }) => {
  const [monitoringData, setMonitoringData] = useState<MonitoringData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState('24h');

  useEffect(() => {
    loadMonitoringData();
  }, [serverId, period]);

  const loadMonitoringData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/servers/${serverId}/monitoring?period=${period}`);
      setMonitoringData(response.data);
      setError(null);
    } catch (error: any) {
      console.error('加载监控数据失败:', error);
      setError(error.response?.data?.message || '加载监控数据失败');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'green';
      case 'stopped': return 'red';
      case 'starting': return 'orange';
      case 'stopping': return 'orange';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'running': return '运行中';
      case 'stopped': return '已停止';
      case 'starting': return '启动中';
      case 'stopping': return '停止中';
      default: return status;
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('zh-CN');
  };

  const getCurrentStats = () => {
    if (!monitoringData) return null;

    const latestResource = monitoringData.resourceUsage[monitoringData.resourceUsage.length - 1];
    const latestPlayer = monitoringData.playerStats[monitoringData.playerStats.length - 1];
    const latestStatus = monitoringData.statusHistory[monitoringData.statusHistory.length - 1];

    return {
      resource: latestResource,
      player: latestPlayer,
      status: latestStatus
    };
  };

  const currentStats = getCurrentStats();

  if (loading) {
    return (
      <Card>
        <div className="text-center py-8">
          <Spin size="large" />
          <div className="mt-4">加载监控数据中...</div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert
          message="加载失败"
          description={error}
          type="error"
          showIcon
          action={
            <button onClick={loadMonitoringData} className="text-blue-500 hover:text-blue-700">
              重试
            </button>
          }
        />
      </Card>
    );
  }

  if (!monitoringData) {
    return (
      <Card>
        <Alert
          message="暂无监控数据"
          description="服务器监控数据暂时不可用"
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <div>
      {/* 服务器基本信息 */}
      <Card className="mb-4">
        <div className="flex justify-between items-center mb-4">
          <Title level={4} className="mb-0">
            <DashboardOutlined className="mr-2" />
            服务器监控
          </Title>
          <Select
            value={period}
            onChange={setPeriod}
            style={{ width: 120 }}
          >
            <Option value="1h">最近1小时</Option>
            <Option value="24h">最近24小时</Option>
            <Option value="7d">最近7天</Option>
          </Select>
        </div>

        <Row gutter={16}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="服务器状态"
                value={getStatusText(monitoringData.server.status)}
                valueStyle={{ 
                  color: getStatusColor(monitoringData.server.status) === 'green' ? '#3f8600' : '#cf1322',
                  fontSize: '16px'
                }}
                prefix={<Tag color={getStatusColor(monitoringData.server.status)} />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="游戏类型"
                value={monitoringData.server.game_name}
                valueStyle={{ fontSize: '16px' }}
                prefix={<DatabaseOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="服务器节点"
                value={monitoringData.server.node_name}
                valueStyle={{ fontSize: '16px' }}
                prefix={<WifiOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="端口"
                value={monitoringData.server.port}
                valueStyle={{ fontSize: '16px' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* 实时状态 */}
      <Row gutter={16} className="mb-4">
        <Col span={8}>
          <Card title="CPU使用率" size="small" style={{ minHeight: '120px' }}>
            {currentStats?.resource && (
              <div>
                <Progress
                  percent={currentStats.resource.cpu_usage}
                  status={currentStats.resource.cpu_usage > 80 ? 'exception' : 'normal'}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
                <Text type="secondary" className="text-sm">
                  {currentStats.resource.cpu_usage}%
                </Text>
              </div>
            )}
          </Card>
        </Col>
        <Col span={8}>
          <Card title="内存使用率" size="small" style={{ minHeight: '120px' }}>
            {currentStats?.resource && (
              <div>
                <Progress
                  percent={currentStats.resource.memory_usage}
                  status={currentStats.resource.memory_usage > 80 ? 'exception' : 'normal'}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
                <Text type="secondary" className="text-sm">
                  {currentStats.resource.memory_usage}%
                </Text>
              </div>
            )}
          </Card>
        </Col>
        <Col span={8}>
          <Card title="在线玩家" size="small" style={{ minHeight: '120px' }}>
            {currentStats?.player && (
              <div>
                <Statistic
                  value={currentStats.player.online_players}
                  suffix={`/ ${currentStats.player.max_players}`}
                  valueStyle={{ fontSize: '20px' }}
                />
                <Progress
                  percent={(currentStats.player.online_players / currentStats.player.max_players) * 100}
                  showInfo={false}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 详细统计 */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="资源使用情况" size="small">
            {currentStats?.resource && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Space>
                    <ThunderboltOutlined />
                    <Text>CPU使用率</Text>
                  </Space>
                  <Text strong>{currentStats.resource.cpu_usage}%</Text>
                </div>
                <div className="flex justify-between items-center">
                  <Space>
                    <CloudOutlined />
                    <Text>内存使用率</Text>
                  </Space>
                  <Text strong>{currentStats.resource.memory_usage}%</Text>
                </div>
                <div className="flex justify-between items-center">
                  <Space>
                    <HddOutlined />
                    <Text>磁盘使用率</Text>
                  </Space>
                  <Text strong>{currentStats.resource.disk_usage}%</Text>
                </div>
                <div className="flex justify-between items-center">
                  <Space>
                    <WifiOutlined />
                    <Text>网络入站</Text>
                  </Space>
                  <Text strong>{formatBytes(currentStats.resource.network_in)}/s</Text>
                </div>
                <div className="flex justify-between items-center">
                  <Space>
                    <WifiOutlined />
                    <Text>网络出站</Text>
                  </Space>
                  <Text strong>{formatBytes(currentStats.resource.network_out)}/s</Text>
                </div>
              </div>
            )}
          </Card>
        </Col>
        <Col span={12}>
          <Card title="玩家统计" size="small">
            {currentStats?.player && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Space>
                    <UserOutlined />
                    <Text>当前在线</Text>
                  </Space>
                  <Text strong>{currentStats.player.online_players} 人</Text>
                </div>
                <div className="flex justify-between items-center">
                  <Space>
                    <UserOutlined />
                    <Text>最大容量</Text>
                  </Space>
                  <Text strong>{currentStats.player.max_players} 人</Text>
                </div>
                <div className="flex justify-between items-center">
                  <Space>
                    <UserOutlined />
                    <Text>加入率</Text>
                  </Space>
                  <Text strong>{currentStats.player.player_join_rate} 人/小时</Text>
                </div>
                <div className="flex justify-between items-center">
                  <Space>
                    <UserOutlined />
                    <Text>离开率</Text>
                  </Space>
                  <Text strong>{currentStats.player.player_leave_rate} 人/小时</Text>
                </div>
                <div className="flex justify-between items-center">
                  <Space>
                    <ClockCircleOutlined />
                    <Text>服务器运行时间</Text>
                  </Space>
                  <Text strong>{currentStats?.status?.uptime || 0}%</Text>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 数据更新时间 */}
      <div className="text-center mt-4">
        <Text type="secondary" className="text-sm">
          数据更新时间: {currentStats?.resource ? formatTime(currentStats.resource.timestamp) : '未知'}
        </Text>
      </div>
    </div>
  );
};

export default ServerMonitoring;
